import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { 
  Clock, 
  Users, 
  MapPin, 
  ChevronDown, 
  ChevronUp,
  Check
} from 'lucide-react';

const EventCard = ({ event, isSelected, onToggleSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card 
      className={`group relative overflow-hidden transition-all duration-300 hover:shadow-xl border-2 rounded-2xl ${
        isSelected 
          ? 'border-green-700 bg-green-50 shadow-lg' 
          : 'border-gray-200 hover:border-green-600 bg-white'
      }`}
    >
      {/* Event Image */}
      {event.image && (
        <div className="relative h-48 overflow-hidden">
          <img 
            src={event.image} 
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          
          {/* Selection Checkbox on Image */}
          <div className="absolute top-3 right-3 z-10">
            <Checkbox
              id={`event-${event.id}`}
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(event.id)}
              className="h-6 w-6 border-2 border-white bg-white/80 data-[state=checked]:bg-green-800 data-[state=checked]:border-green-800 rounded-md"
              aria-label={`Select ${event.title}`}
            />
          </div>
        </div>
      )}

      <CardHeader className="pb-3">
        {/* Category Badge */}
        <div className="mb-3">
          <Badge 
            className="bg-green-100 text-green-900 font-semibold border border-green-300 px-3 py-1"
          >
            {event.category}
          </Badge>
          <h3 className="text-xl font-bold text-gray-900 leading-tight mt-2 group-hover:text-green-800 transition-colors">
            {event.title}
          </h3>
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3 text-green-800" aria-hidden="true" />
            <span className="font-medium text-xs">{event.duration}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-full">
            <MapPin className="w-3 h-3 text-green-800" aria-hidden="true" />
            <span className="font-medium text-xs">{event.format}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-full">
            <Users className="w-3 h-3 text-green-800" aria-hidden="true" />
            <span className="font-medium text-xs">{event.groupSize}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Short Description */}
        <p className="text-gray-600 mb-4 leading-relaxed text-sm">
          {event.shortDescription}
        </p>

        {/* Expandable Content */}
        <div 
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pt-4 border-t border-green-200">
            <p className="text-gray-700 mb-4 text-sm">{event.description}</p>
            
            <h4 className="font-bold text-gray-900 mb-3">Key Highlights:</h4>
            <ul className="space-y-2">
              {event.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-600 text-sm">
                  <span className="w-2 h-2 bg-green-800 rounded-full mt-1.5 flex-shrink-0" aria-hidden="true" />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Toggle Button */}
        <Button
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-4 text-green-800 hover:text-green-900 hover:bg-green-50 font-semibold rounded-xl"
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-5 h-5 mr-2" aria-hidden="true" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-5 h-5 mr-2" aria-hidden="true" />
              Learn More
            </>
          )}
        </Button>

        {/* Select Button */}
        <Button
          onClick={() => onToggleSelect(event.id)}
          className={`w-full mt-3 font-bold text-base py-6 rounded-xl transition-all ${
            isSelected
              ? 'bg-green-800 text-white hover:bg-green-900 shadow-lg'
              : 'bg-green-800 hover:bg-green-900 text-white shadow-md hover:shadow-lg'
          }`}
        >
          {isSelected ? (
            <>
              <Check className="w-5 h-5 mr-2" /> Selected
            </>
          ) : (
            'Select This Event'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default EventCard;
